import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { JobResult } from '../api';
import { awaitJob } from '../awaitJob';
import { ErrorNotice } from '../components/ErrorNotice';
import { fieldLabel, fieldTitle } from '../fieldLabels';
import { canUndo } from '../undoAvailability';

// Job history, and the only place undo is reachable after the moment a job
// finishes. Undo is the feature that makes bulk editing someone else's ERP
// data a reasonable thing to do, so it cannot live only on a screen the user
// has already navigated away from.

interface JobSummary {
  jobId: string;
  entityType: string;
  jobStatus: string;
  jobKind: string;
  createdAt: string;
  completedAt: string | null;
  changeCount: number;
  writtenCount: number;
}

export function JobsPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const api = useApi();

  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);
  const [lastUndo, setLastUndo] = useState<JobResult | null>(null);

  const load = useCallback(async () => {
    if (!api || !tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const { jobs: loaded } = await api.get<{ jobs: JobSummary[] }>(
        `/tenants/${tenantId}/jobs`
      );
      setJobs(loaded);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function undo(jobId: string) {
    if (!api || !tenantId) return;
    setUndoing(jobId);
    setError(null);
    setLastUndo(null);
    try {
      const accepted = await api.post<JobResult>(
        `/tenants/${tenantId}/jobs/${jobId}/undo`,
        {}
      );
      setLastUndo(await awaitJob(api, tenantId, accepted.job.jobId));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Undo failed.');
    } finally {
      setUndoing(null);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={`/tenants/${tenantId}`} className="muted small">
            ← Instance
          </Link>
          <h1>Job history</h1>
        </div>
      </div>

      <ErrorNotice message={error} />

      {lastUndo && (
        <div className="card">
          <h2>Undo complete</h2>
          <p className="muted">
            {lastUndo.changes.filter((c) => c.writeStatus === 'written').length} reverted
            {lastUndo.changes.some((c) => c.writeStatus === 'skipped') &&
              `, ${lastUndo.changes.filter((c) => c.writeStatus === 'skipped').length} left alone`}
            .
          </p>
          <table className="data-table">
            <tbody>
              {lastUndo.changes.map((c) => (
                <tr key={`${c.recordId}:${c.fieldName}`}>
                  <td className="mono">{c.recordId}</td>
                  {/*
                    ⚠ The LABEL, not the raw field name. This table used to
                    print whatever Acumatica calls the field, which matched the
                    preview by coincidence for top-level columns and diverged
                    the moment anyone edited an address: the preview said
                    "Postal code" and the undo log said
                    MainContact/Address/PostalCode. The undo log is the screen
                    where a customer most needs to recognise what changed.
                  */}
                  <td title={fieldTitle(c.fieldName)}>{fieldLabel(c.fieldName)}</td>
                  <td>
                    <span className={`status-pill status-${c.writeStatus}`}>
                      {c.writeStatus}
                    </span>
                  </td>
                  <td className="wrap muted small">{c.errorReason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <p className="muted">Loading…</p>}
      {!loading && jobs.length === 0 && <p className="muted">No jobs yet.</p>}

      {jobs.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Entity</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Written</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.jobId}>
                  <td>{new Date(job.createdAt).toLocaleString()}</td>
                  <td>{job.entityType}</td>
                  <td>{job.jobKind}</td>
                  <td>
                    <span className={`status-pill status-${job.jobStatus}`}>
                      {job.jobStatus}
                    </span>
                  </td>
                  <td>
                    {job.writtenCount} of {job.changeCount}
                  </td>
                  <td>
                    {/* canUndo carries the reasoning, including why a job
                        that is still running must not be offered. */}
                    {canUndo(job) && (
                      <button
                        type="button"
                        onClick={() => void undo(job.jobId)}
                        disabled={undoing !== null}
                      >
                        {undoing === job.jobId ? 'Undoing…' : 'Undo'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
